<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../../provider.js';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import {
  resolveLidMentions,
  linkifyPhoneNumbers,
  WA_MENTION_HREF_PREFIX,
} from 'shared/helpers/lidMentions.js';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS } from '../../constants';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
});

const { variant, renderWhatsappRaw, isWhatsappInbox } = useMessageContext();

const formattedContent = computed(() => {
  // Make phone numbers tappable only inside WhatsApp conversations — a tap starts a
  // WhatsApp chat with that number, so it makes no sense on other channels. Phones
  // first (so the mention links' digits aren't re-linkified), then @LID mentions —
  // both become clickable "#wamention-" links handled below.
  const withPhones = isWhatsappInbox?.value
    ? linkifyPhoneNumbers(props.content)
    : props.content;
  const content = resolveLidMentions(withPhones, {
    linked: true,
  });
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return content;
  }

  // waMode: incoming WhatsApp messages are raw WhatsApp format (* = bold), so render
  // them the WhatsApp way; everything else keeps standard markdown rendering.
  return new MessageFormatter(
    content,
    false,
    false,
    true,
    renderWhatsappRaw?.value
  ).formattedMessage;
});

// A click on a resolved @mention (href "#wamention-<phone>") opens that number's
// chat: the conversation list searches for the phone (and offers "start new chat"
// if none exists) via the OPEN_WA_MENTION bus event handled in ChatList.
const onContentClick = event => {
  const anchor = event.target.closest?.('a');
  const href = anchor?.getAttribute('href') || '';
  if (!href.startsWith(WA_MENTION_HREF_PREFIX)) return;
  event.preventDefault();
  const phone = href.slice(WA_MENTION_HREF_PREFIX.length);
  if (phone) emitter.emit(BUS_EVENTS.OPEN_WA_MENTION, phone);
};
</script>

<template>
  <span
    v-dompurify-html="formattedContent"
    class="prose prose-bubble"
    @click="onContentClick"
  />
</template>
